/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Checkbox, Alert, Space, Tooltip } from 'antd'
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { Input } from 'src/components/Input'
import { Button } from 'src/components/Button'
import { SwitchInput } from 'src/components/ts-components/SwitchInput'
import { NewModal } from 'src/components/NewModal'
import DaySmartLogo from './daysmartlogo.png'
import { Control } from 'react-hook-form'
import React from 'react'
import { paragraphXSmall, weightRegular } from 'src/components/css'

/**
 * Props interface for the DaySmartIntegrationCard component
 */
interface DaySmartIntegrationCardProps {
  /** Whether the user has an Ultimate account */
  isUltimateAccount: boolean
  /** Current API key value */
  apiKey: string
  /** Current secret key value */
  secretKey: string
  /** API key validation error message */
  apiKeyError?: string
  /** Secret key validation error message */
  secretKeyError?: string
  /** General error message */
  error?: string
  /** Whether the user has accepted terms and conditions */
  acceptedTerms: boolean
  /** Whether the integration is currently loading */
  isLoading: boolean
  /** Whether the test API call is in progress */
  isLoadingTest: boolean
  /** Whether the test was successfully submitted */
  testSubmitted: boolean
  /** Whether the API test was successful */
  testSuccess: boolean
  /** React Hook Form control object */
  control: Control<any>
  /** Reference to the integration modal */
  integrationModalRef: React.RefObject<any>
  /** Function to call when switching to Ultimate account page */
  setCurrentPage: (page: number) => void
  /** Actions object containing state setters */
  actions: {
    setAcceptedTerms: (accepted: boolean) => void
  }
  /** Function to handle test button press */
  onPressTest: () => Promise<void>
  /** Function to handle submit/connect button press */
  onPressSubmit: () => Promise<void>
  /** The name of the user's current account type */
  accountTypeName: string
}

/**
 * DaySmartIntegrationCard - A self-contained component for managing DaySmart integration
 *
 * This component handles all UI related to DaySmart integration including:
 * - Integration card display with connection status
 * - Modal form for entering API credentials
 * - Terms and conditions acceptance
 * - Test and connection functionality
 *
 * @param props - Component props containing integration state and handlers
 * @returns JSX element containing the DaySmart integration card and modal
 */
const DaySmartIntegrationCard: React.FC<DaySmartIntegrationCardProps> = ({
  isUltimateAccount,
  apiKey,
  secretKey,
  apiKeyError,
  secretKeyError,
  error,
  acceptedTerms,
  isLoading,
  isLoadingTest,
  testSubmitted,
  testSuccess,
  control,
  integrationModalRef,
  setCurrentPage,
  actions,
  onPressTest,
  onPressSubmit,
  accountTypeName,
}) => {
  console.log('🔄 DaySmartIntegrationCard: Rendering DaySmart integration card')

  const tooltipTitle = (
    <span
      css={css`
        ${paragraphXSmall}
        ${weightRegular}
      `}
    >
      You are currently using a{' '}
      <b
        css={css`
          color: ${colors.PRIMARY};
        `}
      >
        {accountTypeName}
      </b>{' '}
      account.
      <br />
      Upgrade your account to get all the features on the SportNinja platform.{' '}
      <a
        onClick={(e) => {
          e.stopPropagation()
          setCurrentPage(1)
        }}
        css={css`
          color: ${colors.PRIMARY};
          text-decoration: underline;
          cursor: pointer;
        `}
      >
        Click here to upgrade
      </a>
    </span>
  )

  return (
    <>
      {/* DaySmart Integration Card */}
      <Tooltip title={!isUltimateAccount ? tooltipTitle : ''} placement='top'>
        <div
          css={css`
            display: flex;
            padding: 16px;
            flex-direction: column;
            justify-content: space-between;
            flex: 1;
            min-height: 180px;
            border-radius: 4px;
            border: ${apiKey
              ? '1px dashed var(--Neutrals-Gray60, #3f4753)'
              : '1px solid var(--Neutrals-Gray60, #3f4753)'};
            background-color: transparent;
            cursor: pointer;
            transition: background-color 0.2s ease;
            &:hover {
              background-color: rgba(255, 255, 255, 0.05);
            }
            ${!isUltimateAccount &&
            `
              opacity: 0.6;
              cursor: pointer;
              &:hover {
                background-color: rgba(255, 255, 255, 0.05);
              }
            `}
          `}
          onClick={() => {
            console.log('🔄 DaySmartIntegrationCard: Container clicked')
            if (!isUltimateAccount) {
              setCurrentPage(1)
              return
            }
            integrationModalRef?.current?.openModal()
          }}
        >
          <div
            css={css`
              width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
            `}
          >
            <img
              src={DaySmartLogo}
              alt='DaySmart Logo'
              css={css`
                width: 120px;
                height: auto;
              `}
            />
            <span
              css={css`
                color: ${colors.PRIMARY_200};
                text-align: right;
                font-size: 12px;
                font-weight: 400;
                line-height: 100%; /* 12px */
              `}
            >
              Included in Ultimate
            </span>
          </div>
          {!apiKey ? (
            <span
              css={css`
                align-self: stretch;
                color: #fff;
                font-size: 12px;
                font-weight: 500;
                line-height: 120%; /* 14.4px */
                width: 100%;
                margin-top: 16px;
              `}
            >
              Sync or import an entire season&apos;s data, update game
              schedules, and manage player info and team rosters.
            </span>
          ) : (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              <span
                css={css`
                  color: var(--Foundation-Secondary-secondary-25, #a6aab0);
                  font-size: 12px;
                  font-weight: 400;
                  line-height: 145%;
                `}
              >
                Account Details
              </span>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <span
                  css={css`
                    color: #fff;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 145%; /* 20.3px */
                  `}
                >
                  API Key:
                </span>
                <span
                  css={css`
                    color: #fff;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 145%; /* 20.3px */
                    word-break: break-all;
                    overflow-wrap: break-word;
                  `}
                >
                  {apiKey.length > 30 ? apiKey.slice(0, 30) + '...' : apiKey}
                </span>
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <span
                  css={css`
                    color: #fff;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 145%; /* 20.3px */
                  `}
                >
                  API Secret:
                </span>
                <span
                  css={css`
                    color: #fff;
                    font-size: 14px;
                    font-weight: 400;
                    line-height: 145%; /* 20.3px */
                  `}
                >
                  {secretKey.slice(0, 5) + '*****'}
                </span>
              </div>
            </div>
          )}

          {/* DaySmart Switch */}
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 16px;
              padding-top: 16px;
              border-top: 1px solid var(--Neutrals-Gray60, #3f4753);
              margin-top: 16px;
              opacity: 0;
            `}
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            <SwitchInput
              title='Enable Integration'
              subtitle='Allow DaySmart data synchronization'
              value={!!apiKey && !!secretKey}
              onChange={() => {
                if (!isUltimateAccount) {
                  setCurrentPage(1)
                  return
                }
                integrationModalRef?.current?.openModal()
              }}
              disabled={!isUltimateAccount}
            />
          </div>
        </div>
      </Tooltip>

      {/* DaySmart Integration Modal */}
      <NewModal ref={integrationModalRef} shouldCloseOnOverlayClick={true}>
        <div
          css={css`
            width: 45vw;
            background: #26303e;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            transition: all 0.1s ease-in-out;
            border-radius: 8px;
            padding-top: 16px;
          `}
        >
          <button
            onClick={() => {
              integrationModalRef?.current?.closeModal()
            }}
            css={css`
              position: fixed;
              right: 40px;
              top: 40px;
              background: none;
              border: none;
            `}
          >
            <CloseOutlined
              css={css`
                color: ${colors.WHITE};
                font-size: 20px;
              `}
            />
          </button>
          <button
            onClick={() => {
              window.open(
                'https://support.sportninja.com/daysmart-integration-creating-an-api-key',
                '_blank'
              )
            }}
            css={css`
              position: fixed;
              right: 70px;
              top: 40px;
              background: none;
              border: none;
            `}
          >
            <QuestionCircleOutlined
              css={css`
                color: ${colors.WHITE};
                font-size: 20px;
              `}
            />
          </button>
          <div
            css={css`
              padding-left: 16px;
              padding-right: 16px;
              padding-bottom: 16px;
              display: flex;
              flex: 1;
              flex-direction: column;
              height: 100%;
              overflow-y: auto;
              max-height: 550px;
            `}
          >
            {/* Title */}
            <div
              css={css`
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                width: 100%;
                align-items: center;
              `}
            >
              <p
                css={css`
                  font-size: 36px;
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                  font-weight: 700;
                `}
              >
                {'DaySmart Integration'}
              </p>
            </div>
            <div
              css={css`
                border-bottom: 1px solid ${colors.SOFT_STEEL};
                margin-bottom: 16px;
                margin-top: 16px;
              `}
            />
            <div
              css={css`
                gap: 16px;
                display: flex;
                flex-direction: column;
              `}
            >
              <p
                css={css`
                  color: var(--Neutrals-neutrals-0, #fff);
                  font-size: 18px;
                  font-weight: 500;
                  line-height: 145%; /* 26.1px */
                `}
              >
                Please Enter Your Credentials Before Proceeding:
              </p>
              <Input
                fieldName='daysmart_api_key'
                control={control}
                label='DaySmart API Key *'
                required={false}
                invalidMessage={apiKeyError}
                invalid={!!apiKeyError}
                autoComplete='new-password'
              />
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                `}
              >
                <Input
                  fieldName='daysmart_secret_key'
                  control={control}
                  label='API Secret *'
                  required={false}
                  type='password'
                  invalidMessage={secretKeyError}
                  invalid={!!secretKeyError}
                  autoComplete='new-password'
                />
                <p
                  css={css`
                    color: ${colors.PRIMARY};
                    text-align: right;
                    font-size: 12px;
                    font-weight: 400;
                    line-height: 145%; /* 17.4px */
                    cursor: pointer;
                  `}
                  onClick={() => {
                    window.open(
                      'https://support.sportninja.com/daysmart-integration-creating-an-api-key',
                      '_blank'
                    )
                  }}
                >
                  Where To Find This In Daysmart?
                </p>
              </div>
              <div
                css={css`
                  border-bottom: 1px solid ${colors.SOFT_STEEL};
                `}
              />
              <Checkbox
                onChange={() => actions.setAcceptedTerms(!acceptedTerms)}
                checked={acceptedTerms}
              >
                By Proceeding With This Process, You Agree To The{' '}
                <a
                  href='https://www.sportninja.com/integration-terms'
                  target='_blank'
                  rel='noopener noreferrer'
                  css={css`
                    color: ${colors.PRIMARY};
                  `}
                >
                  Terms and Conditions
                </a>{' '}
                Of The Daysmart Integration.
              </Checkbox>

              <div
                css={css`
                  margin-top: 20px;
                  align-self: flex-end;
                  width: 100%;
                  position: sticky;
                  bottom: 0;
                  background-color: ${colors.SECONDARY_300};
                  z-index: 100;
                  padding-top: 16px;
                `}
              >
                {testSubmitted ? (
                  <Space
                    direction='vertical'
                    css={css`
                      margin-bottom: 16px;
                      width: 100%;
                    `}
                  >
                    <Alert
                      message={
                        'API key validated. You can now connect your account.'
                      }
                      type='info'
                      showIcon
                      closable
                    />
                  </Space>
                ) : null}
                {error ? (
                  <Space
                    direction='vertical'
                    css={css`
                      width: 100%;
                      margin-bottom: 16px;
                    `}
                  >
                    <Alert message={error} type='error' showIcon closable />
                  </Space>
                ) : null}
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    width: 100%;
                  `}
                >
                  <Button
                    onClick={(e) => {
                      e.preventDefault()
                      integrationModalRef?.current?.closeModal()
                    }}
                    label={'Cancel'}
                    variant='secondary'
                    disabled={isLoading || isLoadingTest}
                  />
                  <Button
                    onClick={async () => {
                      await onPressTest()
                    }}
                    label={'Test'}
                    variant='secondary'
                    isLoading={isLoadingTest}
                    disabled={isLoading || isLoadingTest || !acceptedTerms}
                  />
                  <Button
                    onClick={async () => {
                      await onPressSubmit()
                    }}
                    type='submit'
                    label='Connect'
                    isLoading={isLoading}
                    disabled={isLoading || !acceptedTerms || !testSuccess}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </NewModal>
    </>
  )
}

export default DaySmartIntegrationCard
